import React, { useState, useEffect } from 'react';
import { AppDrawer } from './ui/AppDrawer';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { IntegerQuantityInput } from './IntegerQuantityInput';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Briefcase, Package, Warehouse, AlertCircle, Check, ArrowLeft, Hammer, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { StockRowChoice, getStockRowOptions, apiErrorText } from './StockRowChoice';

/**
 * AllocateToJobModal - Drawer for job allocations
 * PHASE 2.5C: Allocate (Reserve) → Use (Consume) → Return
 */

const ownershipLabel = (wh) => (wh?.ownership_type === 'consigned'
  ? `Consigned${wh.vendor_name ? ` · ${wh.vendor_name}` : ''}` : 'Owned');

const OwnershipBadge = ({ row }) => (
  <span data-testid={`ownership-badge-${row.ownership_type}`}
    className={`max-w-[11rem] shrink-0 truncate rounded-full px-2 py-0.5 text-[11px] font-medium ${row.ownership_type === 'consigned'
      ? 'bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300'
      : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300'}`}>
    {ownershipLabel(row)}
  </span>
);

const AllocationStatusBadge = ({ status }) => {
  const statusConfig = {
    allocated: { label: 'Allocated', color: 'bg-blue-100 text-blue-800' },
    partially_used: { label: 'Partially Used', color: 'bg-amber-100 text-amber-800' },
    used: { label: 'Used', color: 'bg-green-100 text-green-800' },
    returned: { label: 'Returned', color: 'bg-gray-100 text-gray-800' },
    cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800' },
  };
  const config = statusConfig[status] || statusConfig.allocated;
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${config.color}`}>
      {config.label}
    </span>
  );
};

export const AllocateToJobModal = ({ 
  open, 
  onOpenChange, 
  item, 
  onSuccess 
}) => {
  const [step, setStep] = useState('allocate');
  const [saving, setSaving] = useState(false);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [formData, setFormData] = useState({
    rowId: '', jobNumber: '', quantity: 1, notes: '',
  });
  const [allocation, setAllocation] = useState(null);
  const [actionQuantity, setActionQuantity] = useState(1);
  const [actionNotes, setActionNotes] = useState('');
  const [stockOptions, setStockOptions] = useState(null);
  const [stockRowId, setStockRowId] = useState(null);

  useEffect(() => {
    if (open && item) {
      setStockOptions(null);
      setStockRowId(null);
      const itemId = item?.id || item?.item_id;
      if (itemId) fetchItemWarehouses(itemId);
      setStep('allocate');
      setAllocation(null);
      setFormData({
        rowId: '',
        jobNumber: '', quantity: 1, notes: '',
      });
      setActionQuantity(1);
      setActionNotes('');
    }
  }, [open, item]);

  const fetchItemWarehouses = async (itemId) => {
    setLoadingWarehouses(true);
    try {
      const response = await api.get(`/inventory/item/${itemId}/warehouses`);
      const warehouseData = response.data || [];
      setWarehouses(warehouseData);
      if (warehouseData.length > 0) {
        const preselectId = item?.preselect_warehouse_id;
        const inPreselect = warehouseData.filter(wh => wh.warehouse_id === preselectId);
        const candidates = inPreselect.length ? inPreselect : warehouseData;
        const selectedWarehouse = candidates.reduce((best, wh) => 
          (wh.available > best.available) ? wh : best, candidates[0]);
        setFormData(prev => ({ ...prev, rowId: selectedWarehouse.warehouse_inventory_id }));
      }
    } catch (error) {
      console.error('Failed to fetch warehouses:', error);
      setWarehouses([]);
    } finally {
      setLoadingWarehouses(false);
    }
  };

  const refreshWarehouseData = async () => {
    const itemId = item?.id || item?.item_id;
    if (itemId) {
      try {
        const response = await api.get(`/inventory/item/${itemId}/warehouses`);
        setWarehouses(response.data || []);
      } catch (error) {
        console.error('Failed to refresh warehouses:', error);
      }
    }
  };

  const handleAllocate = async (e) => {
    e.preventDefault();
    if (!formData.rowId) { toast.error('Please select a warehouse'); return; }
    if (!formData.jobNumber.trim()) { toast.error('Job number is required'); return; }
    if (!formData.quantity || formData.quantity < 1) { toast.error('Quantity must be at least 1'); return; }
    const itemId = item?.id || item?.item_id;
    if (!itemId) { toast.error('Cannot identify item to allocate'); return; }
    const selectedWh = warehouses.find(w => w.warehouse_inventory_id === formData.rowId);
    if (selectedWh && formData.quantity > selectedWh.available) {
      toast.error(`Insufficient inventory. Available: ${selectedWh.available}`);
      return;
    }

    setSaving(true);
    try {
      const response = await api.post('/inventory/allocate-to-job', {
        item_id: itemId,
        warehouse_id: selectedWh?.warehouse_id,
        job_number: formData.jobNumber.trim(),
        quantity: formData.quantity,
        notes: formData.notes.trim() || null,
        warehouse_inventory_id: stockRowId || selectedWh?.warehouse_inventory_id || null,
      });
      const data = response.data;
      toast.success(`Allocated ${formData.quantity}x to Job #${data.job_number}`);
      setAllocation({
        id: data.allocation_id, itemName: data.item_name,
        warehouseId: data.warehouse_id, warehouseName: data.warehouse_name, stockLabel: ownershipLabel(selectedWh),
        jobNumber: data.job_number, qtyAllocated: formData.quantity,
        qtyRemaining: formData.quantity, onHand: data.on_hand_qty,
        allocated: data.allocated_qty, status: 'allocated',
      });
      setActionQuantity(formData.quantity);
      setActionNotes('');
      await refreshWarehouseData();
      setStep('success');
      if (onSuccess) onSuccess();
    } catch (error) {
      const options = getStockRowOptions(error);
      if (options) { setStockOptions(options); setStockRowId(null); }
      toast.error(apiErrorText(error, 'Failed to allocate item to job'));
    } finally {
      setSaving(false);
    }
  };

  const handleUse = async () => {
    if (!allocation) return;
    if (!actionQuantity || actionQuantity < 1) { toast.error('Quantity must be at least 1'); return; }
    if (actionQuantity > allocation.qtyRemaining) {
      toast.error(`Cannot use ${actionQuantity}. Only ${allocation.qtyRemaining} remaining.`);
      return;
    }
    setSaving(true);
    try {
      const response = await api.post('/inventory/consume-allocation', {
        allocation_id: allocation.id, quantity_used: actionQuantity,
        notes: actionNotes.trim() || null,
      });
      const data = response.data;
      toast.success(`Used ${actionQuantity} units on Job #${data.job_number}`);
      const newRemaining = data.qty_remaining;
      setAllocation(prev => ({
        ...prev, qtyRemaining: newRemaining,
        onHand: data.warehouse_on_hand, allocated: data.warehouse_allocated_qty,
        status: newRemaining === 0 ? 'used' : 'partially_used',
      }));
      setActionQuantity(newRemaining > 0 ? newRemaining : 0);
      setActionNotes('');
      await refreshWarehouseData();
      setStep('success');
      if (onSuccess) onSuccess();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to use allocation';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Use failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReturn = async () => {
    if (!allocation) return;
    if (!actionQuantity || actionQuantity < 1) { toast.error('Quantity must be at least 1'); return; }
    if (actionQuantity > allocation.qtyRemaining) {
      toast.error(`Cannot return ${actionQuantity}. Only ${allocation.qtyRemaining} remaining.`);
      return;
    }
    setSaving(true);
    try {
      const response = await api.post('/inventory/return-allocation', {
        allocation_id: allocation.id, quantity_returned: actionQuantity,
        notes: actionNotes.trim() || null,
      });
      const data = response.data;
      toast.success(`Released ${actionQuantity} unused ${actionQuantity === 1 ? 'unit' : 'units'} back to available stock`);
      const newRemaining = data.qty_remaining;
      setAllocation(prev => ({
        ...prev, qtyRemaining: newRemaining,
        onHand: data.warehouse_on_hand, allocated: data.warehouse_allocated_qty,
        status: newRemaining === 0 ? 'returned' : 'allocated',
      }));
      setActionQuantity(newRemaining > 0 ? newRemaining : 0);
      setActionNotes('');
      await refreshWarehouseData();
      setStep('success');
      if (onSuccess) onSuccess();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to return allocation';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Return failed');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => onOpenChange(false);

  const itemName = item?.name || item?.item_name || 'Unknown Item';
  const itemSku = item?.sku || '';
  const selectedWarehouse = warehouses.find(w => w.warehouse_inventory_id === formData.rowId);
  const hasWarehouseInventory = warehouses.length > 0;
  const isVehicleOrPartsListOnly = !loadingWarehouses && !hasWarehouseInventory && item;

  const titles = {
    allocate: 'Allocate to Job',
    success: 'Allocation Details',
    use: 'Use on Job',
    return: 'Return to Warehouse',
  };

  const descriptions = {
    allocate: 'Reserve inventory for a job (Allocated).',
    success: 'Manage this allocation - use on job or return to warehouse.',
    use: 'Consume allocated inventory on the job.',
    return: 'Return allocated inventory back to warehouse.',
  };

  const getFooter = () => {
    if (isVehicleOrPartsListOnly) {
      return <Button variant="outline" onClick={handleClose} data-testid="allocate-close-btn">Close</Button>;
    }
    if (step === 'allocate') {
      return (
        <>
          <Button type="button" variant="outline" onClick={handleClose} data-testid="allocate-cancel">Cancel</Button>
          <Button onClick={handleAllocate}
            disabled={saving || !formData.jobNumber.trim() || !formData.rowId || (selectedWarehouse && formData.quantity > selectedWarehouse.available)}
            data-testid="allocate-to-job-open">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Allocating...</> : <><Briefcase className="w-4 h-4 mr-2" />Allocate</>}
          </Button>
        </>
      );
    }
    if (step === 'success') {
      return <Button variant="outline" onClick={handleClose} data-testid="allocate-close-btn">Close</Button>;
    }
    if (step === 'use') {
      return (
        <>
          <Button variant="outline" onClick={() => setStep('success')}>Cancel</Button>
          <Button onClick={handleUse}
            disabled={saving || actionQuantity < 1 || actionQuantity > (allocation?.qtyRemaining || 0)}
            data-testid="consume-submit">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</> : <><Hammer className="w-4 h-4 mr-2" />Use {actionQuantity} Units</>}
          </Button>
        </>
      );
    }
    if (step === 'return') {
      return (
        <>
          <Button variant="outline" onClick={() => setStep('success')}>Cancel</Button>
          <Button onClick={handleReturn}
            disabled={saving || actionQuantity < 1 || actionQuantity > (allocation?.qtyRemaining || 0)}
            data-testid="return-submit">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</> : <><RotateCcw className="w-4 h-4 mr-2" />Return {actionQuantity} Units</>}
          </Button>
        </>
      );
    }
    return null;
  };

  return (
    <AppDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={
        <span className="flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-primary" />
          {titles[step]}
        </span>
      }
      description={descriptions[step]}
      size="md"
      isDirty={step === 'allocate' && (formData.jobNumber.trim() !== '' || formData.notes.trim() !== '')}
      data-testid="allocate-to-job-drawer"
      footer={getFooter()}
    >
      {isVehicleOrPartsListOnly ? (
        <div className="py-6">
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Allocation not available</p>
              <p className="text-sm text-amber-700 mt-1">
                This item is only available in vehicle inventory or the parts list. 
                Allocation is only supported for warehouse inventory.
              </p>
            </div>
          </div>
        </div>
      ) : step === 'allocate' ? (
        <div className="space-y-4">
          {/* Item Info */}
          <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
              <Package className="w-5 h-5 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium truncate">{itemName}</p>
              {itemSku && <p className="text-xs text-muted-foreground font-mono">{itemSku}</p>}
            </div>
          </div>

          {/* Warehouse Selector */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Warehouse *</Label>
            {loadingWarehouses ? (
              <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading warehouses...
              </div>
            ) : warehouses.length > 0 ? (
              <Select value={formData.rowId}
                onValueChange={(value) => { setFormData({ ...formData, rowId: value }); setStockOptions(null); setStockRowId(null); }}>
                <SelectTrigger data-testid="allocate-warehouse-select">
                  <SelectValue placeholder="Select warehouse" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((wh) => (
                    <SelectItem key={wh.warehouse_inventory_id} value={wh.warehouse_inventory_id}
                      data-testid={`allocate-stock-row-${wh.warehouse_inventory_id}`}>
                      <div className="flex min-w-0 items-center gap-2 pr-2">
                        <Warehouse className="w-4 h-4 shrink-0 text-muted-foreground" />
                        <span className="truncate">{wh.warehouse_name}</span>
                        <OwnershipBadge row={wh} />
                        <span className="text-xs text-muted-foreground ml-auto shrink-0">({wh.available} avail)</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="p-3 bg-muted/30 rounded-lg text-sm text-muted-foreground">No warehouse inventory found</div>
            )}
            {selectedWarehouse && (
              <div className="text-xs text-muted-foreground px-1" data-testid="allocate-selected-row-summary">
                {ownershipLabel(selectedWarehouse)} stock · On hand: {selectedWarehouse.on_hand} | Allocated: {selectedWarehouse.allocated} | Available: <span className="font-medium text-foreground">{selectedWarehouse.available}</span>
              </div>
            )}
          </div>

          {/* Job Number */}
          <div className="space-y-2">
            <Label htmlFor="job-number" className="text-sm font-medium">Job Number *</Label>
            <Input id="job-number" value={formData.jobNumber}
              onChange={(e) => setFormData({ ...formData, jobNumber: e.target.value })}
              placeholder="Enter job number" autoFocus data-testid="allocate-job-number" />
          </div>

          {/* Quantity */}
          <div className="space-y-2">
            <Label htmlFor="quantity" className="text-sm font-medium">Quantity *</Label>
            <IntegerQuantityInput id="quantity" min={1} max={selectedWarehouse?.available || 999}
              value={formData.quantity}
              onChange={(val) => setFormData({ ...formData, quantity: Math.max(1, parseInt(val) || 1) })}
              data-testid="allocate-quantity" />
            {selectedWarehouse && formData.quantity > selectedWarehouse.available && (
              <p className="text-xs text-destructive">Exceeds available quantity ({selectedWarehouse.available})</p>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-sm font-medium">
              Notes <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Textarea id="notes" value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional notes..." rows={2} data-testid="allocate-notes" />
          </div>
          <StockRowChoice options={stockOptions} value={stockRowId} onChange={setStockRowId} />
        </div>
      ) : step === 'success' ? (
        <div className="space-y-4">
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Check className="w-5 h-5 text-green-600" />
              <span className="font-medium text-green-800">{allocation?.qtyRemaining > 0 ? 'Allocation Active' : 'Allocation Closed'}</span>
              <AllocationStatusBadge status={allocation?.status} />
            </div>
            <div className="text-sm text-green-700 space-y-1">
              <p><span className="font-medium">Item:</span> {allocation?.itemName}</p>
              <p><span className="font-medium">Job:</span> #{allocation?.jobNumber}</p>
              <p><span className="font-medium">Warehouse:</span> {allocation?.warehouseName}</p>
              {allocation?.stockLabel && <p data-testid="allocation-stock-type"><span className="font-medium">Stock:</span> {allocation.stockLabel}</p>}
            </div>
          </div>
          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Quantity Remaining</span>
              <span className="text-2xl font-bold" data-testid="qty-remaining-label">{allocation?.qtyRemaining || 0}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">Originally allocated: {allocation?.qtyAllocated}</div>
          </div>
          <div className="text-xs text-muted-foreground px-1">
            Warehouse: On hand: {allocation?.onHand} | Allocated: {allocation?.allocated}
          </div>
          {allocation?.qtyRemaining > 0 ? (
            <div className="grid grid-cols-2 gap-3 pt-2">
              <Button variant="default" onClick={() => { setActionQuantity(allocation.qtyRemaining); setActionNotes(''); setStep('use'); }} data-testid="allocation-use-button">
                <Hammer className="w-4 h-4 mr-2" /> Use on Job
              </Button>
              <Button variant="outline" onClick={() => { setActionQuantity(allocation.qtyRemaining); setActionNotes(''); setStep('return'); }} data-testid="allocation-return-button">
                <RotateCcw className="w-4 h-4 mr-2" /> Return
              </Button>
            </div>
          ) : (
            <div className="p-3 bg-muted rounded-lg text-center text-sm text-muted-foreground">
              Allocation complete - no remaining quantity
            </div>
          )}
        </div>
      ) : step === 'use' ? (
        <div className="space-y-4">
          <Button variant="ghost" size="sm" onClick={() => setStep('success')} className="mb-2 -ml-2">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="p-3 bg-muted/50 rounded-lg text-sm">
            <p><span className="font-medium">Job:</span> #{allocation?.jobNumber}</p>
            <p><span className="font-medium">Available to use:</span> {allocation?.qtyRemaining}</p>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Quantity to Use *</Label>
            <IntegerQuantityInput min={1} max={allocation?.qtyRemaining || 1} value={actionQuantity}
              onChange={(val) => setActionQuantity(Math.max(1, Math.min(allocation?.qtyRemaining || 1, parseInt(val) || 1)))}
              data-testid="consume-quantity" />
            {actionQuantity > (allocation?.qtyRemaining || 0) && (
              <p className="text-xs text-destructive">Exceeds remaining ({allocation?.qtyRemaining})</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Notes <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Textarea value={actionNotes} onChange={(e) => setActionNotes(e.target.value)}
              placeholder="Usage notes..." rows={2} data-testid="consume-notes" />
          </div>
        </div>
      ) : step === 'return' ? (
        <div className="space-y-4">
          <Button variant="ghost" size="sm" onClick={() => setStep('success')} className="mb-2 -ml-2">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="p-3 bg-muted/50 rounded-lg text-sm">
            <p><span className="font-medium">Warehouse:</span> {allocation?.warehouseName}</p>
            <p><span className="font-medium">Available to return:</span> {allocation?.qtyRemaining}</p>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Quantity to Return *</Label>
            <IntegerQuantityInput min={1} max={allocation?.qtyRemaining || 1} value={actionQuantity}
              onChange={(val) => setActionQuantity(Math.max(1, Math.min(allocation?.qtyRemaining || 1, parseInt(val) || 1)))}
              data-testid="return-quantity" />
            {actionQuantity > (allocation?.qtyRemaining || 0) && (
              <p className="text-xs text-destructive">Exceeds remaining ({allocation?.qtyRemaining})</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Notes <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Textarea value={actionNotes} onChange={(e) => setActionNotes(e.target.value)}
              placeholder="Return notes..." rows={2} data-testid="return-notes" />
          </div>
        </div>
      ) : null}
    </AppDrawer>
  );
};
